import React from 'react';
import { Typography } from 'antd';
import styled from 'styled-components';
import tw from 'twin.macro';
import { Stack } from 'components/stack/stack';
import { Text } from 'components/text/Text';
import { OfferResponse } from 'logic/api-models/api-models';
import { translate, translationKeys } from 'logic/translations/translations.service';
import { TeachingLevel } from 'logic/api-models/teaching-level';
import { translateTeachingLevel } from 'logic/api-models/teaching-level.logic';
import { TutorClassLocation } from './TutorClassLocation';
import { useBreakpoints } from 'logic/hooks/use-breakpoints';
import { SchoolOfferGroup } from './SchoolOfferGroup';
import getIsTutoringSchool from 'logic/helpers/isTutoringSchool';
import isPayPerLead from 'logic/helpers/isPayPerLead';
import { isElitetutor, isRefugeeTutor } from 'logic/helpers/tutorHelper';
import { useLocation } from 'react-use';
import { useLocalizedI18nGlobal } from '../../../../logic/translations/use-localized-i18n-global';

const { Paragraph } = Typography;

const ContactList = styled.ul<any>`
  && li {
    padding: 0;
    list-style-type: none;
    margin-bottom: 0.5rem;
    margin-left: 0;
    &:first-child {
      margin-top: 1rem;
    }
    & span {
      display: block;
    }
  }
`;

const TutorOfferTypes = styled.div<any>`
  display: flex;
  flex-direction: ${({ min1024 }) => (min1024 ? 'row' : 'column')};
  padding-right: ${({ min1024 }) => (min1024 ? '2rem' : '0')};
  margin-bottom: 1em;
  justify-content: space-between;
  gap: 1rem;
`;

const TutorDescriptionContainer = styled.div`
  ${tw`pt-8`}
`;

export const TutorDescription: React.FC<{
  offer: OfferResponse | undefined;
}> = ({ offer }) => {
  useLocalizedI18nGlobal();
  const { min1024 } = useBreakpoints();
  const TutoringSchool = getIsTutoringSchool(offer);
  const location = useLocation();
  const _isElitetutor = isElitetutor(offer, location?.pathname);
  const _isRefugeeTutor = isRefugeeTutor(offer, location.pathname);
  const PayPerLead = isPayPerLead(offer);
  return (
    <TutorDescriptionContainer>
      <Stack gap={0.5} flow="row">
        <div>
          <Text color="primary" size={14} strong>
            {translate(translationKeys.offerDetails.description)}
          </Text>
          <Paragraph>
            {offer?.offerDesc?.split('\n').map((textLine, index) => (
              <Text key={index} size={14} textAlign="left">
                {textLine}
                <br />
              </Text>
            ))}
          </Paragraph>
        </div>
        <div>
          {!_isRefugeeTutor && (
            <Paragraph>
              <Text color="primary" size={14} strong>
                {translate(translationKeys.offerDetails.teachingLevelUpTo)}{' '}
              </Text>
              {_isElitetutor && (
                <Text size={14}>{translateTeachingLevel(TeachingLevel.abitur)}</Text>
              )}
              {!_isElitetutor && offer?.teachingLevel !== undefined && (
                <Text size={14}>
                  {translateTeachingLevel(offer?.teachingLevel as TeachingLevel)}
                </Text>
              )}
            </Paragraph>
          )}
        </div>
        {!_isElitetutor && (
          <TutorOfferTypes min1024={min1024}>
            <TutorClassLocation offer={offer} />
            {TutoringSchool && <SchoolOfferGroup offer={offer} />}
          </TutorOfferTypes>
        )}
        {PayPerLead && !_isElitetutor && (
          <div>
            <Paragraph>
              <Text color="primary" size={14} strong>
                {translate(translationKeys.offerDetails.contact)}
              </Text>
              <ContactList>
                <li>
                  <Text size={14} strong>
                    {translate(translationKeys.offerDetails.contactPerson)}
                  </Text>
                  <Text size={14}>
                    {offer?.tutoringSchool?.primaryContact
                      ? offer?.tutoringSchool?.primaryContact
                      : translate(translationKeys.offerDetails.notProvided)}
                  </Text>
                </li>
                <li>
                  <Text size={14} strong>
                    {translate(translationKeys.offerDetails.phone)}
                  </Text>
                  <Text size={14}>
                    {offer?.tutoringSchool?.phoneNumber
                      ? offer?.tutoringSchool?.phoneNumber
                      : translate(translationKeys.offerDetails.notProvided)}
                  </Text>
                </li>
                <li>
                  <Text size={14} strong>
                    {translate(translationKeys.offerDetails.email)}
                  </Text>
                  <Text size={14}>
                    {offer?.tutoringSchool?.email
                      ? offer?.tutoringSchool?.email
                      : translate(translationKeys.offerDetails.notProvided)}
                  </Text>
                </li>
              </ContactList>
            </Paragraph>
          </div>
        )}
      </Stack>
    </TutorDescriptionContainer>
  );
};
